const getters = {
	/**
	 * Retorna o ID de sinais de tendência de frequência selecionadas.
	 * @returns {Array<number>} Uma lista dos IDs de sinais de tendências de frequência selecionadas.
	 */
	selectedFrequencyTrendSettingsIds(state)
	{
		return state.selectedFrequencyTrendSettings.map((el) => el.frequencyTrendSettingId);
	},

	/**
	 * Retorna os sinais de tendência atualmente selecionados.
	 * @returns {Array<Object>} Lista de sinais selecionados.
	 */
	selectedFrequencyTrendSettings(state)
	{
		return state.selectedFrequencyTrendSettings;
	},

	/**
	 * Verifica se os sinais de tendência foram selecionados.
	 * @returns {boolean} True se os sinais de tendência foram selecionados, false caso contrário.
	 */
	isFrequencySignalsSelectedValid(state)
	{
		return !!state.selectedFrequencyTrendSettings && state.selectedFrequencyTrendSettings.length > 0;
	},

	/**
	 * Conta quantos sinais comuns estão selecionados.
	 * @returns {number} Quantidade de sinais comuns selecionados.
	 */
	commonSelectedCount: (state, getters, rootState) =>
	{
		return new Set(
			(state.selectedFrequencyTrendSettings || [])
				.filter(s => (rootState.Trend?.TrendMenu?.FrequencyTrendSignals?.availableCommonSignals || [])
					.some(common => common.name === s.name))
				.map(s => s.name)
		).size;
	},

	/**
	 * Retorna o nome do ícone para indicar se o grupo de sinais comuns está expandido.
	 * @param {Object} state - Estado local do módulo Vuex.
	 * @returns {string} Nome do ícone do Material Design.
	 */
	isCommonGroupIconExpanded: (state) =>
	{
		return state.CommonGroupExpanded ? "mdi-chevron-up" : "mdi-chevron-down";
	},

	/**
	 * Verifica se um determinado sinal está selecionado.
	 * @returns {function(Object): boolean} Função que recebe um sinal e retorna true se ele está selecionado.
	 */
	isSelected: (state) => (signal) =>
	{
		const signalAsset = signal.asset || null;

		if (!Array.isArray(state.selectedFrequencyTrendSettings)) return false;

		return state.selectedFrequencyTrendSettings.some(
			(s) => s.name === signal.name && (s.asset || null) === signalAsset
		);
	},

	/**
	 * Conta quantos sinais distintos estão selecionados.
	 * @returns {number} Quantidade de sinais distintos selecionados.
	 */
	distinctSelectedCount: (state, getters, rootState) =>
	{
		const distinctGroups = rootState.Trend.TrendMenu.FrequencyTrendSignals.availableDistinctSignalGroups || [];
		let count = 0;

		distinctGroups.forEach((group) =>
		{
			const signals = group.signals || [];
			signals.forEach((signal) =>
			{
				if (getters.isSelected(signal))
				{
					count++;
				}
			});
		});

		return count;
	},

	/**
	 * Retorna o nome do ícone para indicar se o grupo de sinais distintos está expandido.
	 * @returns {string} Nome do ícone do Material Design.
	 */
	isDistinctGroupIconExpanded: (state) =>
	{
		return state.DistinctGroupExpanded ? "mdi-chevron-up" : "mdi-chevron-down";
	},

	/**
   * Retorna os nomes únicos dos sinais selecionados.
   * @returns {Array} Nomes únicos.
   */
	uniqueSignalNames: (state) =>
	{
		const signals = state.selectedFrequencyTrendSettings || [];
		const seen = new Set();
		signals.forEach(s => { if (s && s.name) seen.add(s.name); });
		return Array.from(seen);
	},
	/**
   * Retorna os primeiros dois sinais selecionados, com nomes únicos por nome.
   * @returns {Array} Até 2 sinais únicos por nome.
   */
	firstTwoSelectedSignals: (state) =>
	{
		const signals = state.selectedFrequencyTrendSettings || [];
		const uniqueByName = [];
		const seenNames = new Set();

		for (const s of signals)
		{
			if (s && s.name && !seenNames.has(s.name))
			{
				uniqueByName.push(s);
				seenNames.add(s.name);
			}
		}

		return uniqueByName.slice(0, 2);
	},

	/**
	 * Retorna todos os grupos de ativos distintos expandidos.
	 * @returns {Array<string>} Array com os nomes dos ativos expandidos.
	 */
	expandedDistinctAssetGroups: (state) => state.expandedDistinctAssetGroups,
	/**
	 * Retorna os sinais comuns filtrados pelo texto do filtro.
	 * Se o filtro estiver vazio, retorna todos os sinais comuns disponíveis.
	 * @returns {Array} Lista de sinais comuns filtrados.
	 */
	filteredCommonSignals(state)
	{
		if (!state.filterText)
		{
			const seen = new Set();
			return state.availableCommonSignals.filter(signal =>
			{
				const key = `${signal.name}-${signal.assetName || ""}`;
				if (seen.has(key)) return false;
				seen.add(key);
				return true;
			});
		}

		const text = state.filterText.toLowerCase();
		const seen = new Set();
		return state.availableCommonSignals
			.filter(signal => signal.name.toLowerCase().includes(text))
			.filter(signal =>
			{
				const key = `${signal.name}-${signal.assetName || ""}`;
				if (seen.has(key)) return false;
				seen.add(key);
				return true;
			});
	},
	/**
	 * Retorna os grupos de sinais distintos filtrados pelo texto do filtro.
	 * Cada grupo terá apenas os sinais cujo nome contenha o texto do filtro.
	 * Se o filtro estiver vazio, retorna todos os grupos de sinais distintos disponíveis.
	 * Grupos sem sinais filtrados são excluídos.
	 * @returns {Array} Lista de grupos de sinais distintos filtrados.
	 */
	filteredDistinctSignalGroups(state)
	{
		if (!state.filterText)
		{
			return state.availableDistinctSignalGroups.map(group =>
			{
				const seen = new Set();
				return {
					...group,
					signals: group.signals.filter(signal =>
					{
						const key = `${signal.name}-${signal.assetName || ""}`;
						if (seen.has(key)) return false;
						seen.add(key);
						return true;
					})
				};
			});
		}

		const text = state.filterText.toLowerCase();
		return state.availableDistinctSignalGroups
			.map(group =>
			{
				const seen = new Set();
				const filtered = group.signals
					.filter(signal => signal.name.toLowerCase().includes(text))
					.filter(signal =>
					{
						const key = `${signal.name}-${signal.assetName || ""}`;
						if (seen.has(key)) return false;
						seen.add(key);
						return true;
					});

				return {
					...group,
					signals: filtered
				};
			})
			.filter(group => group.signals.length > 0);
	},
};

export default { getters };